import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { formatDate, formatFileSize } from '../helpers';
import { launchColors } from '../launchTheme';
import { Assignee, LaunchFile } from '../types';

interface FolderOption {
  id: string;
  name: string;
}

interface Props {
  files: LaunchFile[];
  folders: FolderOption[];
  assignees: Assignee[];
  showActivity?: boolean;
  busy?: boolean;
  onOpenActivity?: (activityId: string) => void;
  onDownload: (file: LaunchFile) => void;
  onDelete: (file: LaunchFile) => void;
  onMove: (file: LaunchFile, folderId: string) => void;
}

const AttachmentList: React.FC<Props> = ({
  files, folders, assignees, showActivity = false, busy = false, onOpenActivity, onDownload, onDelete, onMove,
}) => {
  const [pendingDelete, setPendingDelete] = useState<LaunchFile | null>(null);
  const [moveTarget, setMoveTarget] = useState<{ file: LaunchFile; anchor: HTMLElement } | null>(null);
  const personName = (userId: string) => assignees.find((person) => person.id === userId)?.name || 'Okänd person';

  if (!files.length) return null;

  return (
    <>
      <Stack spacing={0} component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
        {files.map((file) => (
          <Stack
            component="li"
            key={file.id}
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ sm: 'center' }}
            sx={{ py: 1, borderBottom: `1px solid ${launchColors.rowDivider}`, '&:last-of-type': { borderBottom: 'none' } }}
          >
            <InsertDriveFileOutlinedIcon sx={{ color: launchColors.navy, display: { xs: 'none', sm: 'block' } }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography noWrap sx={{ fontSize: 14, fontWeight: 600 }}>{file.fileName}</Typography>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
                <Typography sx={{ fontSize: 12, color: launchColors.textSecondary }}>{file.folderName}</Typography>
                {showActivity && file.activityId && (
                  <Button size="small" sx={{ px: 0, minWidth: 0, fontSize: 12 }} onClick={() => onOpenActivity?.(file.activityId as string)}>
                    {file.activityTitle || 'Visa uppgift'}
                  </Button>
                )}
                <Typography sx={{ fontSize: 12, color: launchColors.textSecondary }}>
                  {personName(file.uploadedBy)} · {formatDate(file.uploadedAt)} · {formatFileSize(file.sizeBytes)}
                </Typography>
              </Stack>
            </Box>
            <Stack direction="row" spacing={0.5}>
              <IconButton aria-label={`Ladda ner ${file.fileName}`} size="small" disabled={busy} onClick={() => onDownload(file)}>
                <DownloadOutlinedIcon fontSize="small" />
              </IconButton>
              {file.canMove && (
                <IconButton
                  aria-label={`Flytta ${file.fileName}`}
                  size="small"
                  disabled={busy}
                  onClick={(event) => setMoveTarget({ file, anchor: event.currentTarget })}
                >
                  <DriveFileMoveOutlinedIcon fontSize="small" />
                </IconButton>
              )}
              {file.canDelete && (
                <IconButton aria-label={`Ta bort ${file.fileName}`} size="small" disabled={busy} onClick={() => setPendingDelete(file)}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              )}
            </Stack>
          </Stack>
        ))}
      </Stack>
      <Menu anchorEl={moveTarget?.anchor} open={Boolean(moveTarget)} onClose={() => setMoveTarget(null)}>
        {folders.filter((folder) => folder.id !== moveTarget?.file.folderId).map((folder) => (
          <MenuItem
            key={folder.id}
            onClick={() => {
              if (moveTarget) onMove(moveTarget.file, folder.id);
              setMoveTarget(null);
            }}
          >
            {folder.name}
          </MenuItem>
        ))}
      </Menu>
      <Dialog open={Boolean(pendingDelete)} onClose={() => setPendingDelete(null)} fullWidth maxWidth="xs">
        <DialogTitle>Ta bort fil</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14 }}>
            {pendingDelete ? `${pendingDelete.fileName} tas bort för alla i lanseringen. Borttagningen sparas i historiken.` : ''}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingDelete(null)}>Avbryt</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              if (pendingDelete) onDelete(pendingDelete);
              setPendingDelete(null);
            }}
          >
            Ta bort
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AttachmentList;
